// MCP tab (Agent Platform Phase 5): connect AI agents to KaziPeople.
//
// Shows the MCP endpoint, this tenant's agent-kind API clients, the governed
// tool catalog (from the capability registry, surface=mcp; reads plus a few
// confirm-gated, user-consented actions), and
// ready-to-paste client configs. Connecting an agent = creating a kind=agent
// API client with narrow scopes — the same credential model as any
// integration, so everything here links back to the API clients tab.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Bot, ExternalLink, KeyRound, ShieldCheck } from 'lucide-react';
import {
  Capability, type ApiClient, isNotEntitled, isNotRolledOut,
  listApiClients, listCapabilities,
  getMcpAnalytics, listTenantAuthorizations, revokeTenantAuthorization,
  type McpAnalytics, type TenantAuthorization,
} from '@/services/developerService';
import { ApprovalGrantsCard } from './ApprovalGrantsCard';
import { DanaMcpServersCard } from './DanaMcpServersCard';
import { isEntitlementUnavailable } from '@/lib/apiError';
import { CopyButton, FeatureNotEnabledState, RolloutPendingState, fmtDateTime, TemporarilyUnavailableState, errMsg } from './common';

// Per-environment MCP endpoint. Dev builds talk to the dev server; prod to the
// canonical hostname once DNS lands (until then the fly.dev name serves).
const MCP_URL = import.meta.env.VITE_MCP_URL
  || (window.location.hostname.includes('dev.') ? 'https://mcp.dev.kazipeople.com' : 'https://mcp.kazipeople.com');

const claudeConfig = `{
  "mcpServers": {
    "kazipeople": {
      "url": "${MCP_URL}",
      "headers": { "Authorization": "Bearer <ACCESS_TOKEN>" }
    }
  }
}`;

const tokenCurl = (api: string) => `curl -s ${api}/public/v1/oauth/token \\
  -H 'Content-Type: application/json' \\
  -d '{"grant_type":"client_credentials","client_id":"<kpc_...>","client_secret":"<kps_...>"}'`;

// Per-provider connect instructions (universal-LLM Phase 3). OAuth providers
// self-register (DCR) and walk the consent screen — no keys to copy; the JSON
// snippets are for clients configured by file.
const oauthJson = `{
  "mcpServers": {
    "kazipeople": { "url": "${MCP_URL}" }
  }
}`;

const PROVIDERS: { key: string; label: string; steps: string[]; snippet?: string }[] = [
  {
    key: 'claude',
    label: 'claude.ai',
    steps: [
      'In Claude: Settings → Connectors → Add custom connector.',
      `Name it "KaziPeople", URL: ${MCP_URL} — leave the OAuth fields empty.`,
      'Click Connect, sign in to your workspace, review scopes, Allow.',
    ],
  },
  {
    key: 'claude-desktop',
    label: 'Claude Desktop / Code',
    steps: ['Add to your MCP config — the browser opens for consent on first use:'],
    snippet: oauthJson,
  },
  {
    key: 'cursor',
    label: 'Cursor / IDEs',
    steps: ['Add to ~/.cursor/mcp.json — Cursor opens the consent screen on first use:'],
    snippet: oauthJson,
  },
  {
    key: 'chatgpt',
    label: 'ChatGPT',
    steps: [
      'Enable Developer mode: ChatGPT → Settings → Connectors → Advanced.',
      `Create a connector named "KaziPeople", MCP server URL: ${MCP_URL}, authentication: OAuth.`,
      'Approve the KaziPeople consent screen, then enable the connector in a conversation.',
    ],
  },
];

export const McpTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<ApiClient[] | null>(null);
  const [provider, setProvider] = useState('claude');
  const [analytics, setAnalytics] = useState<McpAnalytics | null>(null);
  const [sessions, setSessions] = useState<TenantAuthorization[] | null>(null);
  const [revokingFam, setRevokingFam] = useState<string | null>(null);

  useEffect(() => {
    getMcpAnalytics().then(setAnalytics).catch(() => setAnalytics(null));
    listTenantAuthorizations().then(setSessions).catch(() => setSessions(null));
  }, []);

  const revokeSession = async (row: TenantAuthorization) => {
    setRevokingFam(row.familyId);
    try {
      await revokeTenantAuthorization(row.familyId);
      setSessions(await listTenantAuthorizations());
    } catch {
      // toast handled globally by interceptors elsewhere; keep quiet here
    } finally {
      setRevokingFam(null);
    }
  };
  const [capabilities, setCapabilities] = useState<Capability[] | null>(null);
  const [err, setErr] = useState<any>(null);

  useEffect(() => {
    Promise.all([listApiClients(), listCapabilities({ surface: 'mcp' })])
      .then(([c, caps]) => { setClients(c); setCapabilities(caps); })
      .catch(setErr);
  }, []);

  const agents = useMemo(() => (clients || []).filter((c) => c.kind === 'agent'), [clients]);
  const apiBase = window.location.hostname.includes('dev.')
    ? 'https://api.dev.kazipeople.com' : 'https://api.kazipeople.com';
  const domains = useMemo(() => {
    const byDomain = new Map<string, Capability[]>();
    for (const c of capabilities || []) {
      if (!byDomain.has(c.domain)) byDomain.set(c.domain, []);
      byDomain.get(c.domain)!.push(c);
    }
    return [...byDomain.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [capabilities]);

  if (err && isEntitlementUnavailable(err)) return <TemporarilyUnavailableState message={errMsg(err, '')} />;
  if (err && isNotRolledOut(err)) return <RolloutPendingState message={t('developer.mcp_rollout', { defaultValue: 'The MCP surface is being rolled out to your workspace.' })} />;
  if (err && isNotEntitled(err)) return <FeatureNotEnabledState message={t('developer.mcp_not_entitled', { defaultValue: 'The Developer Platform is not enabled on your plan.' })} />;

  return (
    <div className="space-y-6">
      {/* Endpoint + model */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-medium text-charcoal flex items-center gap-2">
          <Bot className="w-4 h-4" />
          {t('developer.mcp_title', { defaultValue: 'Connect AI agents (MCP)' })}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          {t('developer.mcp_intro', {
            defaultValue:
              'KaziPeople speaks the Model Context Protocol so Claude, ChatGPT, IDE agents and your own agents can look up workforce, payroll and payment data — limited to the scopes you grant, with every call flowing through the same API gateway, rate limits and audit trail as any integration. A small set of actions (submitting leave, managing webhook endpoints) exists too, and only on user-consented connections with an explicit in-conversation confirmation.',
          })}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <code className="text-sm bg-muted px-2 py-1 rounded">{MCP_URL}</code>
          <CopyButton text={MCP_URL} label={t('developer.copy', { defaultValue: 'Copy' })} />
        </div>
        <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
          <span>
            {t('developer.mcp_safety', {
              defaultValue:
                'Agents can never move money, approve payroll, or bypass approvals through MCP. Financial actions have no agent surface at all, and the few non-financial actions that exist require a consenting user plus an explicit confirmation on every call.',
            })}
          </span>
        </div>
      </div>

      {/* Per-provider OAuth connect (no keys) */}
      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h3 className="font-medium text-charcoal">{t('developer.mcp_connect_assistant', { defaultValue: 'Connect an assistant (OAuth — no keys)' })}</h3>
        <p className="text-sm text-muted-foreground">
          {t('developer.mcp_connect_help', { defaultValue: 'Assistants register themselves and each user consents with their own account — tokens rotate automatically and can be revoked from Settings → Security → Connected apps.' })}
        </p>
        <div className="flex flex-wrap gap-2">
          {PROVIDERS.map((p) => (
            <button key={p.key} type="button" onClick={() => setProvider(p.key)}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${provider === p.key ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/[0.08] text-foreground' : 'border-border text-muted-foreground hover:bg-muted/40'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {(() => {
          const p = PROVIDERS.find((x) => x.key === provider) || PROVIDERS[0];
          return (
            <div className="space-y-2">
              <ol className="list-decimal ml-5 space-y-1 text-sm text-foreground">
                {p.steps.map((step) => <li key={step}>{step}</li>)}
              </ol>
              {p.snippet && (
                <div className="relative">
                  <pre className="text-xs bg-muted rounded p-3 overflow-x-auto">{p.snippet}</pre>
                  <div className="absolute top-2 right-2"><CopyButton text={p.snippet} label="" /></div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Setup steps */}
      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h3 className="font-medium text-charcoal">{t('developer.mcp_setup', { defaultValue: 'Set up a headless connection (API client + token)' })}</h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm text-foreground">
          <li>
            {t('developer.mcp_step1', { defaultValue: 'Create an API client with kind "Agent" and ONLY the scopes the agent needs (least privilege):' })}{' '}
            <button className="text-orange-600 hover:underline inline-flex items-center gap-1"
              onClick={() => setSearchParams({ tab: 'clients' }, { replace: true })}>
              <KeyRound className="w-3.5 h-3.5" />
              {t('developer.mcp_open_clients', { defaultValue: 'API clients tab' })}
            </button>
          </li>
          <li>{t('developer.mcp_step2', { defaultValue: 'Fetch an access token with the client credentials:' })}
            <div className="mt-1 relative">
              <pre className="text-xs bg-muted rounded p-3 overflow-x-auto">{tokenCurl(apiBase)}</pre>
              <div className="absolute top-2 right-2"><CopyButton text={tokenCurl(apiBase)} label="" /></div>
            </div>
          </li>
          <li>{t('developer.mcp_step3', { defaultValue: 'Point your MCP client at the endpoint with the Bearer token — Claude/Cursor example:' })}
            <div className="mt-1 relative">
              <pre className="text-xs bg-muted rounded p-3 overflow-x-auto">{claudeConfig}</pre>
              <div className="absolute top-2 right-2"><CopyButton text={claudeConfig} label="" /></div>
            </div>
          </li>
          <li>{t('developer.mcp_step4', { defaultValue: 'Ask the agent to call capabilities_list — it reports exactly what the connection may do.' })}</li>
        </ol>
      </div>

      {/* Agent connections */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-medium text-charcoal">{t('developer.mcp_agents', { defaultValue: 'Agent connections' })}</h3>
        {clients === null ? (
          <p className="text-sm text-muted-foreground mt-2">{t('common.loading', { defaultValue: 'Loading…' })}</p>
        ) : agents.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-2">
            {t('developer.mcp_no_agents', { defaultValue: 'No agent clients yet — create one on the API clients tab with kind "Agent".' })}
          </p>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-3">{t('developer.mcp_col_name', { defaultValue: 'Name' })}</th>
                <th className="py-1.5 pr-3">{t('developer.mcp_col_scopes', { defaultValue: 'Scopes' })}</th>
                <th className="py-1.5 pr-3">{t('developer.mcp_col_status', { defaultValue: 'Status' })}</th>
                <th className="py-1.5">{t('developer.mcp_col_seen', { defaultValue: 'Last used' })}</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} className="border-b border-border">
                  <td className="py-2 pr-3 text-foreground">{a.name}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.scopes.join(', ')}</td>
                  <td className="py-2 pr-3">{a.status}</td>
                  <td className="py-2 text-muted-foreground">{fmtDateTime(a.lastUsedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* MCP analytics (Phase 5): live aggregates from the MCP server */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-medium text-charcoal">{t('developer.mcp_analytics', { defaultValue: 'Tool-call analytics' })}</h3>
        {analytics === null ? (
          <p className="text-sm text-muted-foreground mt-2">
            {t('developer.mcp_analytics_none', { defaultValue: 'No analytics yet — they appear once an agent makes tool calls.' })}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mt-1">
              {t('developer.mcp_analytics_since', {
                defaultValue: '{{total}} calls ({{mutations}} actions) across {{clients}} connection(s) since {{since}}. Live counters — the audit log is the permanent record.',
                total: analytics.total,
                mutations: analytics.mutations,
                clients: Object.keys(analytics.byClient || {}).length,
                since: fmtDateTime(analytics.since),
              })}
            </p>
            {(analytics.tools || []).length > 0 && (
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3">{t('developer.mcp_col_tool', { defaultValue: 'Tool' })}</th>
                    <th className="py-1.5 pr-3">{t('developer.mcp_col_calls', { defaultValue: 'Calls' })}</th>
                    <th className="py-1.5 pr-3">{t('developer.mcp_col_errors', { defaultValue: 'Errors' })}</th>
                    <th className="py-1.5 pr-3">{t('developer.mcp_col_latency', { defaultValue: 'Avg / max ms' })}</th>
                    <th className="py-1.5">{t('developer.mcp_col_last', { defaultValue: 'Last call' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(analytics.tools || [])].sort((a, b) => b.calls - a.calls).map((s2) => (
                    <tr key={s2.tool} className="border-b border-border">
                      <td className="py-2 pr-3">
                        <span className="font-mono text-foreground">{s2.tool}</span>
                        {s2.mutating && (
                          <span className="ml-1.5 text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-warning-soft text-warning-strong border border-warning">
                            {t('developer.mcp_action', { defaultValue: 'action' })}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-foreground">{s2.calls}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{s2.errors || '—'}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{Math.round(s2.avgLatencyMs)} / {s2.maxLatencyMs}</td>
                      <td className="py-2 text-muted-foreground">{fmtDateTime(s2.lastCalledAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Delegated sessions (Phase 5): which users linked which AI clients */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-medium text-charcoal">{t('developer.mcp_sessions', { defaultValue: 'Delegated connections (whole company)' })}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t('developer.mcp_sessions_help', { defaultValue: 'Every live user-consented AI/app connection in this tenant. Revoking cuts that connection at its next token refresh — useful for off-boarding.' })}
        </p>
        {sessions === null || sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-2">
            {t('developer.mcp_sessions_none', { defaultValue: 'No delegated connections yet.' })}
          </p>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-3">{t('developer.mcp_col_user', { defaultValue: 'User' })}</th>
                <th className="py-1.5 pr-3">{t('developer.mcp_col_app', { defaultValue: 'App / agent' })}</th>
                <th className="py-1.5 pr-3">{t('developer.mcp_col_scopes', { defaultValue: 'Scopes' })}</th>
                <th className="py-1.5 pr-3">{t('developer.mcp_col_refreshed', { defaultValue: 'Last refresh' })}</th>
                <th className="py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((row) => (
                <tr key={row.familyId} className="border-b border-border">
                  <td className="py-2 pr-3 text-foreground">{row.userEmail}</td>
                  <td className="py-2 pr-3">
                    <span className="text-foreground">{row.appName}</span>
                    <span className="ml-1.5 text-xs text-muted-foreground">{row.source === 'dcr' ? t('developer.self_registered', { defaultValue: 'self-registered' }) : row.kind}</span>
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground truncate max-w-[220px]">{row.scopes.join(', ')}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{fmtDateTime(row.lastRefreshedAt)}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      disabled={revokingFam === row.familyId}
                      onClick={() => void revokeSession(row)}
                      className="text-xs text-red-600 hover:underline disabled:opacity-50"
                    >
                      {revokingFam === row.familyId ? t('common.working', { defaultValue: 'Working…' }) : t('developer.mcp_revoke', { defaultValue: 'Revoke' })}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tool catalog */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-medium text-charcoal">{t('developer.mcp_tools', { defaultValue: 'Available capabilities' })}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t('developer.mcp_tools_hint', {
            defaultValue: 'An agent only sees the capabilities whose scope its client holds — the governed registry below is the full surface.',
          })}
        </p>
        {capabilities === null ? (
          <p className="text-sm text-muted-foreground mt-2">{t('common.loading', { defaultValue: 'Loading…' })}</p>
        ) : (
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {domains.map(([domain, caps]) => (
              <div key={domain} className="rounded border border-border p-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{domain}</p>
                <ul className="mt-1.5 space-y-1">
                  {caps.map((c) => (
                    <li key={c.id} className="text-sm text-foreground flex items-baseline justify-between gap-2">
                      <span>
                        {c.title}
                        {c.mutating && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide rounded bg-orange-100 text-orange-700 px-1 py-0.5 align-middle">
                            {t('developer.mcp_action_badge', { defaultValue: 'action · confirm' })}
                          </span>
                        )}
                      </span>
                      <code className="text-xs text-muted-foreground shrink-0">{c.required_scope ?? '—'}</code>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <a
          href="https://www.kazipeople.com/developers/docs/mcp"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline mt-4"
        >
          {t('developer.mcp_docs', { defaultValue: 'MCP documentation' })} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      <ApprovalGrantsCard />

      <DanaMcpServersCard />
    </div>
  );
};
